import React, { useState, useEffect } from 'react';
import { ppeApi } from '../services/api';
import UploadPPEModal from './UploadPPEModal';
import '../styles/Panel.css';

function PPEManagementPanel({ entity, onComplete }) {
  const [farStatus, setFarStatus] = useState(null);
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (entity) {
      fetchFARStatus();
      fetchOutputs();
    }
  }, [entity]);

  const fetchFARStatus = async () => {
    try {
      setLoading(true);
      const data = await ppeApi.getFARStatus(entity.name);
      setFarStatus(data);
    } catch (err) {
      console.error('Failed to fetch FAR status:', err);
      setError('Failed to load FAR status');
    } finally {
      setLoading(false);
    }
  };

  const fetchOutputs = async () => {
    try {
      const data = await ppeApi.listPPEOutputs(entity.name);
      setOutputs(data.outputs || []);
    } catch (err) {
      console.error('Failed to fetch PPE outputs:', err);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setError(null);
      const blob = await ppeApi.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'far_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleDownloadFARData = async () => {
    try {
      setError(null);
      const blob = await ppeApi.downloadFARData(entity.name);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `far_input_${entity.name}_${farStatus.latest_version.timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err){
      setError('Failed to download FAR data: ' + err.message);
    }
  };

  const handleDownloadOutput = async (output) => {
    try {
      setError(null);
      const blob = await ppeApi.downloadPPEOutput(entity.name, output.period, output.timestamp);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ppe_report_${entity.name}_${output.period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download output: ' + err.message);
    }
  };

  const handleGeneratePPEReport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      const result = await ppeApi.calculatePPEReport(entity.name, startDate, endDate);

      // Refresh outputs list
      await fetchOutputs();

      // Clear date inputs
      setStartDate('');
      setEndDate('');

      alert(`PPE report generated successfully for period ${startDate} to ${endDate}`);
    } catch (err) {
      setError('Failed to generate PPE report: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    fetchFARStatus();
    if (onComplete) onComplete();
  };

  if (loading && !farStatus) {
    return (
      <div className="panel ppe-panel">
        <h2>PPE Management</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="panel ppe-panel">
      <h2>Property, Plant & Equipment (PPE)</h2>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Action Buttons */}
      <div className="button-group">
        <button
          className="btn btn-secondary"
          onClick={handleDownloadTemplate}
        >
          Download Template
        </button>
        <button
          className="btn btn-primary"
          onClick={() => setShowUploadModal(true)}
        >
          Upload FAR Data
        </button>
      </div>

      {/* FAR Data Section */}
      {farStatus && farStatus.has_data ? (
        <div className="section">
          <h3>Fixed Asset Register (FAR)</h3>
          <div className="info-card">
            <div className="info-row">
              <span className="info-label">Uploaded:</span>
              <span className="info-value">
                {new Date(farStatus.latest_version.upload_time).toLocaleString()}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Total Assets:</span>
              <span className="info-value">{farStatus.latest_version.asset_count}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Asset Classes:</span>
              <span className="info-value">
                {farStatus.latest_version.asset_classes.length > 0
                  ? farStatus.latest_version.asset_classes.join(', ')
                  : 'N/A'}
              </span>
            </div>
          </div>
          <div className="button-group">
            <button
              className="btn btn-secondary"
              onClick={handleDownloadFARData}
            >
              Download FAR Data
            </button>
          </div>

          {/* Generate PPE Report Section */}
          <div className="section" style={{ marginTop: '20px' }}>
            <h4>Generate PPE Report</h4>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Start Date:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="date-input"
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>End Date:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="date-input"
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={handleGeneratePPEReport}
                disabled={generating || !startDate || !endDate}
                style={{ marginTop: '20px' }}
              >
                {generating ? 'Generating...' : 'Generate PPE Report'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="section">
          <div className="alert alert-info">
            No Fixed Asset Register (FAR) data available. Please upload FAR data to get started.
          </div>
        </div>
      )}

      {/* PPE Report Outputs Section */}
      {outputs.length > 0 && (
        <div className="section">
          <h3>Previous PPE Reports</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Generated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {outputs.map((output, idx) => (
                <tr key={idx}>
                  <td>{output.period}</td>
                  <td>{output.start_date || 'N/A'}</td>
                  <td>{output.end_date || 'N/A'}</td>
                  <td>
                    {output.upload_time
                      ? new Date(output.upload_time).toLocaleString()
                      : 'N/A'}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleDownloadOutput(output)}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadPPEModal
          entity={entity}
          onClose={() => setShowUploadModal(false)}
          onComplete={handleUploadComplete}
        />
      )}
    </div>
  );
}

export default PPEManagementPanel;
